import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {CApi as userGwApi} from '@services/api';
import {isReturnsEnabled} from '@services/isReturnsEnabled';
import {AppContext} from '@app/context';

const POLL_INTERVAL_MS = 60_000;
const DOCUMENT_NOTIFICATION_TTL_MS = 24 * 60 * 60 * 1000;

type NotificationBase = {
    id: string;
    type: string;
    payload?: unknown;
    readAt?: string;
    createdAt: string;
};

type UnreadNotificationsResponse = {
    total: number;
    items: NotificationBase[];
};

type DocumentReceivedPayload = {
    docType?: string;
    count?: number | string;
    requestId?: string;
};

type RefusalFinishedPayload = {
    accepted?: string | number;
    declined?: string | number;
    count?: string | number;
    orderItemPk?: string | number;
    orderItemPkType?: string;
    time?: string;
};

type ReturnStatusCode =
    | 'new'
    | 'processing'
    | 'declined'
    | 'accepted'
    | 'returned'
    | 'paid'
    | 'wait_expertise'
    | 'on_expertise'
    | 'returned_after_expertise'
    | 'canceled';

type ReturnStatusPayload = {
    previous?: ReturnStatusCode[];
    current?: ReturnStatusCode[];
    returnId?: number;
    comment?: string;
    orderItemPk?: string | number;
    orderItemPkType?: string;
};

export type DocumentReceivedNotification = NotificationBase & {
    type: 'documents_received';
    payload?: DocumentReceivedPayload | null;
};

export type RefusalFinishedNotification = NotificationBase & {
    type: 'refusal_finished';
    payload?: RefusalFinishedPayload | null;
};

export type ReturnStatusNotification = NotificationBase & {
    type: 'return_status';
    payload?: ReturnStatusPayload | null;
};

type DocumentNotificationsContextValue = {
    notifications: DocumentReceivedNotification[];
    refusalFinishedNotifications: RefusalFinishedNotification[];
    returnStatusNotifications: ReturnStatusNotification[];
    unreadDocumentsCount: number;
    unreadReturnsCount: number;
    notificationsKey: string;
    returnStatusNotificationsKey: string;
    toastTitle: string;
    dismissedKey: string | null;
    dismissCurrentToast: () => void;
    refresh: () => Promise<void>;
    markAllAsRead: () => Promise<void>;
    markRefusalFinishedAsRead: () => Promise<void>;
    markReturnStatusAsRead: () => Promise<void>;
};

const DocumentNotificationsContext = createContext<DocumentNotificationsContextValue | null>(null);

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
};

const unwrapResponseData = <T,>(response: T | {data: T}): T => {
    if (isRecord(response) && 'data' in response) {
        return response.data as T;
    }

    return response as T;
};

const isDocumentReceivedNotification = (
    item: NotificationBase
): item is DocumentReceivedNotification => {
    return item.type === 'documents_received';
};

const isRefusalFinishedNotification = (
    item: NotificationBase
): item is RefusalFinishedNotification => {
    return item.type === 'refusal_finished';
};

const isReturnStatusNotification = (item: NotificationBase): item is ReturnStatusNotification => {
    return item.type === 'return_status';
};

const hasReturnStatusPayload = (
    item: ReturnStatusNotification
): item is ReturnStatusNotification & {payload: ReturnStatusPayload} => {
    return item.payload != null;
};

const normalizeDateString = (value: string): string => {
    return value.replace(/\.(\d{3})\d+(?=(Z|[+-]\d{2}:\d{2})$)/, '.$1');
};

const parseDateMs = (value: string): number => {
    if (!value) {
        return Number.NaN;
    }

    const normalizedValue = normalizeDateString(value);
    return new Date(normalizedValue).getTime();
};

const isFreshDocumentNotification = (item: NotificationBase): boolean => {
    const createdAtMs = parseDateMs(item.createdAt);

    if (Number.isNaN(createdAtMs)) {
        return false;
    }

    return Date.now() - createdAtMs < DOCUMENT_NOTIFICATION_TTL_MS;
};

const toPositiveNumber = (value: unknown): number => {
    if (typeof value === 'number') {
        return Number.isFinite(value) && value > 0 ? value : 0;
    }

    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }

    return 0;
};

const getDocumentsCount = (item: DocumentReceivedNotification): number => {
    const count = toPositiveNumber(item.payload?.count);

    return count > 0 ? count : 1;
};

const getUniqueNotificationIds = (items: NotificationBase[]): string[] => {
    return Array.from(new Set(items.map((item) => item.id).filter(Boolean)));
};

export const DocumentNotificationsProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    const {
        state: {clientDetails}
    } = useContext(AppContext);
    const isReturnsVisible = isReturnsEnabled(clientDetails?.login);

    const [allUnreadNotifications, setAllUnreadNotifications] = useState<NotificationBase[]>([]);
    const [dismissedKey, setDismissedKey] = useState<string | null>(null);

    const inFlightRef = useRef(false);
    const isMountedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const refresh = useCallback(async () => {
        if (!isReturnsVisible) {
            return;
        }

        if (document.visibilityState === 'hidden') {
            return;
        }

        if (inFlightRef.current) {
            return;
        }

        inFlightRef.current = true;

        try {
            const response = await userGwApi().unreadNotifications.unreadNotificationsList();
            const data = unwrapResponseData<UnreadNotificationsResponse>(response);

            const items = Array.isArray(data?.items) ? data.items : [];
            const unreadItems = items.filter((item) => !item.readAt);

            if (isMountedRef.current) {
                setAllUnreadNotifications(unreadItems);
            }
        } catch {
            // noop
        } finally {
            inFlightRef.current = false;
        }
    }, [isReturnsVisible]);

    useEffect(() => {
        if (!isReturnsVisible) {
            return;
        }
        
        void refresh();

        const intervalId = window.setInterval(() => {
            if (document.visibilityState === 'visible') {
                void refresh();
            }
        }, POLL_INTERVAL_MS);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void refresh();
            }
        };

        const handleFocus = () => {
            void refresh();
        };

        const handleOnline = () => {
            void refresh();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('online', handleOnline);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('online', handleOnline);
        };
    }, [refresh, isReturnsVisible]);

    const notifications = useMemo(() => {
        return allUnreadNotifications
            .filter(isDocumentReceivedNotification)
            .filter(isFreshDocumentNotification);
    }, [allUnreadNotifications]);

    const refusalFinishedNotifications = useMemo(() => {
        return allUnreadNotifications.filter(isRefusalFinishedNotification);
    }, [allUnreadNotifications]);

    const allReturnStatusNotifications = useMemo(() => {
        return allUnreadNotifications.filter(isReturnStatusNotification);
    }, [allUnreadNotifications]);

    const returnStatusNotifications = useMemo(() => {
        return allReturnStatusNotifications.filter(hasReturnStatusPayload);
    }, [allReturnStatusNotifications]);

    const notificationsKey = useMemo(() => {
        return notifications
            .map((item) => item.id)
            .sort()
            .join('|');
    }, [notifications]);

    const returnStatusNotificationsKey = useMemo(() => {
        return allReturnStatusNotifications
            .map((item) => item.id)
            .sort()
            .join('|');
    }, [allReturnStatusNotifications]);

    const unreadDocumentsCount = useMemo(() => {
        return notifications.reduce((acc, item) => acc + getDocumentsCount(item), 0);
    }, [notifications]);

    const unreadReturnsCount = useMemo(() => {
        return returnStatusNotifications.length;
    }, [returnStatusNotifications]);

    const toastTitle =
        unreadDocumentsCount > 1
            ? `Получены новые документы (${unreadDocumentsCount})`
            : 'Получен новый документ';

    const dismissCurrentToast = useCallback(() => {
        setDismissedKey(notificationsKey);
    }, [notificationsKey]);

    const markAllAsRead = useCallback(async () => {
        const uniqueIds = getUniqueNotificationIds(notifications);

        if (!uniqueIds.length) {
            return;
        }

        await Promise.allSettled(
            uniqueIds.map((id) => userGwApi().readNotifications.readNotificationsCreate({id}))
        );
    }, [notifications]);

    const markRefusalFinishedAsRead = useCallback(async () => {
        const uniqueIds = getUniqueNotificationIds(refusalFinishedNotifications);

        if (!uniqueIds.length) {
            return;
        }

        await Promise.allSettled(
            uniqueIds.map((id) => userGwApi().readNotifications.readNotificationsCreate({id}))
        );
    }, [refusalFinishedNotifications]);

    const markReturnStatusAsRead = useCallback(async () => {
        const uniqueIds = getUniqueNotificationIds(allReturnStatusNotifications);

        if (!uniqueIds.length) {
            return;
        }

        await Promise.allSettled(
            uniqueIds.map((id) => userGwApi().readNotifications.readNotificationsCreate({id}))
        );
    }, [allReturnStatusNotifications]);

    const value = useMemo<DocumentNotificationsContextValue>(() => {
        return {
            notifications,
            refusalFinishedNotifications,
            returnStatusNotifications,
            unreadDocumentsCount,
            unreadReturnsCount,
            notificationsKey,
            returnStatusNotificationsKey,
            toastTitle,
            dismissedKey,
            dismissCurrentToast,
            refresh,
            markAllAsRead,
            markRefusalFinishedAsRead,
            markReturnStatusAsRead
        };
    }, [
        notifications,
        refusalFinishedNotifications,
        returnStatusNotifications,
        unreadDocumentsCount,
        unreadReturnsCount,
        notificationsKey,
        returnStatusNotificationsKey,
        toastTitle,
        dismissedKey,
        dismissCurrentToast,
        refresh,
        markAllAsRead,
        markRefusalFinishedAsRead,
        markReturnStatusAsRead
    ]);

    return (
        <DocumentNotificationsContext.Provider value={value}>
            {children}
        </DocumentNotificationsContext.Provider>
    );
};

export const useDocumentNotifications = () => {
    const context = useContext(DocumentNotificationsContext);

    if (!context) {
        throw new Error(
            'useDocumentNotifications must be used inside DocumentNotificationsProvider'
        );
    }

    return context;
};
